import React, { useEffect, useState } from 'react'
import { Button, Card, Container, Form, Row } from 'react-bootstrap'
import AsyncSelect from 'react-select/async';
import KWAdminNavbar from '../../components/navbar/AdminNav'
import axiosInstance from '../../utils/AxiosInstance'
import Tabledata from '../../components/appoinment/Index'
import { toast, ToastContainer } from 'react-toastify'
import moment from 'moment'
import Select from 'react-select'

/**
 * NoticeToEnterForm component for generating and sending Notice to Enter notifications to tenants
 * This form allows property managers to select properties, tenants, date/time, and reason for entry
 */
const NoticeToEnterForm = () => {
    const [isLoading, setIsLoading] = useState(false); // State to manage loading state of the page
    const [isSending, setIsSending] = useState(false); // State to manage sending state of the mail
    const [Options, setOptions] = useState([]) // State to manage property options
    const [selectedOp, setSelectedOpt] = useState() // State to manage selected property
    const [TableData, setTableData] = useState([]) // State to manage table data
    const [selectedRows, setSelectedRows] = useState([]) // State to manage selected rows
    const [dateOfEntry, setDateOfEntry] = useState('') // State to manage date of entry
    const [reason, setReason] = useState('') // State to manage reason for entry
    const [reasons, setReasons] = useState([]) // State to manage reason options
    const [clearRows, setClearRows] = useState(false) // State to manage clearing selected rows
    const [allEvents, setAllEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState();
    const [defaultOptions, setDefaultOptions] = useState([]); // State for default options

    const columns = [
        {
            name: "First Name",
            selector: row => row.FirstName,
            sortable: true
        },
        {
            name: "Last Name",
            selector: row => row.LastName,
            sortable: true
        },
        {
            name: "Email",
            selector: row => row.Email,
            sortable: true
        },
        {
            name: "Unit",
            selector: row => row.UnitNumber,
            sortable: true
        },
        {
            name: "Phone",
            selector: row => row.Phone,
            sortable: true
        },
        {
            name: "Property Address",
            selector: row => row.PropertyAddress,
            sortable: true
        }

    ];

    /**
     * Validates that the selected date is in the future and at least 24 hours from now
     * @param {string} dateString - The date string to validate
     * @returns {boolean} - Whether the date is valid
     */
    const validateDate = (dateString) => {
      const selectedDate = new Date(dateString);
      const now = new Date();
      const minDate = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now
      
      if (selectedDate < minDate) {
        toast.error('Please select a date at least 24 hours in advance');
        return false;
      }
      return true;
    }

    /**
     * This function clears all the state values.
     */
    function clearAllState() {
        setSelectedOpt()
        setSelectedRows([])
        setDateOfEntry('')
        setReason('')
        setClearRows(true)
        setTimeout(() => {
            setClearRows(false)
        }, 1000)
    }
    
    /**
     * This function fetches tenant data for a given property ID and updates the
     * table data state with the fetched data.
     */
    const getPropertyById = async (id) => {
        // Set the loading state to true before starting the API call
        setIsLoading(true)

        // Clear the table data state before fetching new data
        setTableData([])
        setReasons([])
        try {
            // Input validation with clear error messaging
            if (!id || !id.PId) {
                throw new Error('Invalid property selection. Missing property ID.');
            }
            
            // Extract the numeric part of the property ID from the provided id object
            const PId = id.PId?.replace(/\D/g, "");
            
            if (!PId) {
                throw new Error('Invalid property ID format.');
            }

            // Make an API call to fetch tenant data for the given property ID
            const result = await axiosInstance().get(`/v1/listing/property/${PId}/tenant`)

            // Response validation
            if (!result || !result.data) {
                throw new Error('Invalid response received from server.');
            }

            // Update the table data state with the fetched tenant data
            if (!Array.isArray(result.data.tenants)) {
                try {
                    setTableData(JSON.parse(result.data.tenants));
                } catch (parseError) {
                    console.error('Error parsing tenants data:', parseError);
                    setTableData([]);
                    toast.warning('Received unexpected tenant data format.');
                }
            } else {
                setTableData(result.data.tenants);
                
                // Notify if no tenants found
                if (result.data.tenants.length === 0) {
                    toast.info('No tenants found for this property.');
                }
            }
            
            // Validate and process reasons data
            if (!Array.isArray(result.data.nteReasons) || result.data.nteReasons.length === 0) {
                toast.warning('No entry reasons available for this property.');
                setReasons([]);
            } else {
                setReasons(() =>
                    result.data.nteReasons.map((val, i) => {
                        if (i === 0) {
                            setReason({
                                value: val.id,
                                label: (
                                    <div style={{ textAlign: 'left' }}>
                                        <strong>{val.reason}</strong>
                                    </div>
                                ),
                            });
                        }
                        return {
                            value: val.id,
                            label: (
                                <div style={{ textAlign: 'left' }}>
                                    <strong>{val.reason}</strong>
                                </div>
                            )
                        }
                    })
                )
            }
            
            // Set the loading state to false after successfully fetching the data
            setIsLoading(false)
        } catch (error) {
            // Set the loading state to false in case of an error
            setIsLoading(false)

            // Log the error to the console for debugging purposes
            console.error('Error fetching tenant data:', error);
            
            // Provide user-friendly error messages based on error type
            if (error.message && !error.response) {
                // Local error (not from API)
                toast.error(error.message);
            } else if (error.response) {
                // API error responses
                switch (error.response.status) {
                    case 400:
                        toast.error('Invalid request. Please check your selection and try again.');
                        break;
                    case 401:
                    case 403:
                        toast.error('You do not have permission to access this information.');
                        break;
                    case 404:
                        toast.error('Property not found. It may have been removed.');
                        break;
                    case 500:
                        toast.error('Server error. Our team has been notified.');
                        break;
                    default:
                        toast.error(error.response?.data?.message || 'Failed to load tenant data. Please try again.');
                }
            } else {
                // Network or other errors
                toast.error('Network error. Please check your connection and try again.');
            }
        }
    }

    // Function to fetch options (used by loadOptions and initial load)
    const fetchOptions = async (inputValue = '') => {
        try {
            const result = await axiosInstance().get('/v1/showing/public');
        const events = result.data.groupedEvents;
        const newArr = [];
        for (let key in events) newArr.push(...events[key]);

       setAllEvents(newArr);

            const options = newArr.map(val => ({
                value: val.extendedProperties.shared,
                label: (
                    <div style={{ textAlign: 'left' }}>
                        <strong>{val.extendedProperties.shared.propertyTitle}</strong>
                        <br />
                        <span style={{ fontSize: 'smaller', color: '#888',textAlign:'left' }}>{val.extendedProperties.shared.unitTitle}</span>
                    </div>
                )
            }))

            // Filter options based on inputValue if provided
            return options.filter(option =>
                option.value.propertyTitle.toLowerCase().includes(inputValue.toLowerCase())
            );
        } catch (error) {
            console.error('Error loading options:', error);
            return [];
        }
    };

    // Load options on component mount
    useEffect(() => {
        const loadInitialOptions = async () => {
            const options = await fetchOptions();
            setDefaultOptions(options);
        };
        loadInitialOptions();
    }, []);

    // Load options for AsyncSelect
    const loadOptions = async (inputValue, callback) => {
        const options = await fetchOptions(inputValue);
        callback(options);
    };

    const handleMailSend = async () => {
        
        try {
            // Validate inputs
            if (!dateOfEntry || !validateDate(dateOfEntry)) {
                return;
            }
            if(!reason?.value){
                toast.error('Please select a reason')
                return;
            }
            if (!selectedRows?.length) {
                toast.error('Please select at least one tenant');
                return;
            }
            
            // Add confirmation dialog for multiple tenants
            if (selectedRows.length > 1) {
                if (!window.confirm(`Are you sure you want to send notices to ${selectedRows.length} tenants?`)) {
                    return;
                }
            }
            
            setIsSending(true)
            const data = {
                "dateOfEntry": dateOfEntry,
                "reason": reason.value,
                "tenants": selectedRows
            }
            const result = await axiosInstance().post('/v1/showing/notice-to-enter', { ...data })
            if (result.status === 200) {
                setIsSending(false)
                clearAllState()
                toast.success(`Successfully sent notices to ${selectedRows.length} tenant(s)`)
            }
        } catch (error) {
            setIsSending(false)
            console.error(error)
            toast.error(error.response?.data?.message || 'Failed to send mail')
        }
    }


    /**
     * Handles the change event for row selection in a table.
     */
    const handleRowChange = (selectedRow) => setSelectedRows(selectedRow.selectedRows);
    useEffect(() => {
        if (selectedEvent) {
            setDateOfEntry(moment(selectedEvent?.start?.dateTime).format('YYYY-MM-DDTHH:mm'));
        }
    }, [selectedEvent]);

    return (
        <>
            <ToastContainer />
            <KWAdminNavbar />
            <Container className='mt-5 pt-5'>
                <Row>
                    <div className='col-lg-6 col-sm-10'>
                        <Form.Group>
                            <Form.Label>Property</Form.Label>
                            <AsyncSelect
                                cacheOptions
                                defaultOptions={defaultOptions}
                                loadOptions={loadOptions}
                                placeholder="Select Property"
                                value={selectedOp}
                                onChange={(opt) => {
                                    if (opt) {
                                        setSelectedEvent(() =>
                                            allEvents.find(
                                                (event) => event.extendedProperties.shared === opt.value
                                            )
                                        );
                                        setSelectedOpt(opt);
                                        getPropertyById(opt.value);
                                    } else {
                                        setSelectedOpt(null);
                                        setTableData([]);
                                        setReasons([]);
                                    }
                                }}
                                isClearable
                            />
                        </Form.Group>
                    </div>
                </Row>
                <Row>
                    <div className='col-lg-12 col-sm-10'>
                        <>
                            <div className='col-lg-6 col-sm-10 mt-3 mt-lg-5'>
                                <div className='row me-1 my-1'>
                                    <label className='col-3' htmlFor="dateOfEntry" aria-required="true"><strong>Date :</strong></label>
                                    <input
                                        className='rounded border col-9 p-1'
                                        style={{ outlineColor: '#007bff' }}
                                        type="datetime-local"
                                        id="dateOfEntry"
                                        value={dateOfEntry}
                                        onChange={(e) => setDateOfEntry(e.target.value)}
                                        name="dateOfEntry"
                                        aria-label="Date and time for entry notice"
                                        min={new Date(Date.now() + 86400000).toISOString().slice(0, 16)} // 24 hours from now
                                        required />
                                </div>
                                <div className='row my-1 me-1'>
                                    <label className='col-3' htmlFor="reason" aria-required="true"><strong>Reason :</strong></label>
                                    <Select
                                        className='rounded col-9 py-1 px-0'
                                        options={reasons}
                                        placeholder="Select a reason"
                                        value={reason}
                                        onChange={(opt) => setReason(opt)}
                                        inputId="reason"
                                        aria-label="Reason for entry"
                                        required
                                    />
                                </div>
                            </div>
                            <div className='d-flex justify-content-end mb-2'>
                                <Button disabled={(selectedRows.length === 0 || isSending)} onClick={handleMailSend}>
                                    {isSending?(
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ):"Send"}
                                </Button>
                            </div>
                            <Card className='showings-card'>
                                <Card.Header className='showings-card-header'>
                                    <h5>Notice To Enter</h5>
                                </Card.Header>
                            </Card>
                            <div className='table-container'>
                                <Tabledata
                                    onRowChange={handleRowChange}
                                    // selectableRowsNoSelectAll
                                    clear={clearRows}
                                    selectableRows={true} columns={columns}
                                    isLoading={isLoading} data={TableData} />
                            </div>
                        </>
                    </div>
                </Row>
            </Container>
        </>
    )
}

export default NoticeToEnterForm
